import { lazy, Suspense } from 'react';
import { MapPin } from 'lucide-react';
import { useAuthStore, useManualLocationStore } from '../../stores';
import { StatusBadge } from '../ui';
import { useFloatingAction } from '../layout/floatingActions';

interface ManualLocationFabProps {
    enabled?: boolean;
}

const ManualLocationPanel = lazy(() =>
    import('./ManualLocationPanel').then((module) => ({ default: module.ManualLocationPanel }))
);

export function ManualLocationFab({ enabled = false }: ManualLocationFabProps) {
    const user = useAuthStore((state) => state.user);
    const manualModeEnabled = useManualLocationStore((state) => state.manualModeEnabled);
    const isPanelOpen = useManualLocationStore((state) => state.isPanelOpen);
    const openPanel = useManualLocationStore((state) => state.openPanel);

    const content = (
        <button
            type="button"
            aria-label={manualModeEnabled
                ? 'Abrir panel de ubicación manual. Ubicación simulada activa.'
                : 'Abrir panel de ubicación manual de debug.'}
            aria-pressed={manualModeEnabled}
            onClick={openPanel}
            className={`flex min-h-12 max-w-full touch-manipulation items-center gap-2 rounded-full border px-3 py-3 text-sm font-semibold shadow-lg transition-[border-color,background-color,transform] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 sm:px-4 ${
                manualModeEnabled
                    ? 'border-warning/35 bg-warning text-black hover:bg-warning/90'
                    : 'border-border bg-surface text-text hover:border-primary/30 hover:bg-hover'
            }`}
        >
            <span
                className={`inline-flex size-6 shrink-0 items-center justify-center rounded-full ${
                    manualModeEnabled ? 'bg-black/10' : 'bg-primary/10 text-primary'
                }`}
                aria-hidden="true"
            >
                <MapPin className="size-4" />
            </span>
            <span className="whitespace-nowrap">
                {manualModeEnabled ? (
                    <>
                        <span className="sm:hidden">Simulada</span>
                        <span className="hidden sm:inline">Ubicación simulada</span>
                    </>
                ) : (
                    'Ubicación'
                )}
            </span>
        </button>
    );

    useFloatingAction({
        id: 'manual-location',
        order: 30,
        visible: enabled && user !== null,
        content,
    });

    return (
        <>
            {manualModeEnabled ? (
                <div
                    className="pointer-events-none fixed left-1/2 top-[calc(0.75rem+var(--spacing-safe-top,0px))] z-[940] w-[calc(100%-2rem)] -translate-x-1/2 text-center"
                    role="status"
                    aria-live="polite"
                >
                    <StatusBadge
                        status="warning"
                        label="MODO DEBUG: ubicación simulada"
                        dot
                        className="max-w-full border border-warning/25 bg-warning/15 px-3 py-1 text-warning shadow-lg backdrop-blur"
                    />
                </div>
            ) : null}
            {isPanelOpen ? (
                <Suspense fallback={null}>
                    <ManualLocationPanel />
                </Suspense>
            ) : null}
        </>
    );
}
